<template>
  <HrPageLayout
    title="Leave Planning"
    subtitle="Department leave schedules, allowance tracking, terminal leave notifications"
    icon="📅"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Leave & Planning' }, { label: 'Leave Planning' }]"
  >
    <div class="director-module">
      <div class="tabs-row">
        <button v-for="t in tabs" :key="t.id" type="button" class="tab-btn" :class="{ active: activeTab === t.id }" @click="activeTab = t.id; load()">
          {{ t.label }}
        </button>
      </div>

      <div v-show="activeTab === 'schedules'" class="card-section">
        <div class="filters-row">
          <label>Year</label>
          <select v-model="scheduleYear" @change="loadSchedules">
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
          <select v-model="filterDeptId" @change="loadSchedules">
            <option value="">All departments</option>
            <option v-for="d in departments" :key="d.id" :value="d.departmentID || d.id">{{ d.departmentName }}</option>
          </select>
          <button type="button" class="hr-btn hr-btn-primary" @click="openScheduleModal">Add schedule</button>
        </div>
        <div class="table-wrap">
          <table class="table table-bordered table-sm">
            <thead>
              <tr><th>Department</th><th>Leave type</th><th>Year</th><th>Planned dates</th><th>Notes</th><th>Actions</th></tr>
            </thead>
            <tbody>
              <tr v-for="s in schedules" :key="s.id">
                <td>{{ s.department_name }}</td>
                <td>{{ s.leave_type_name || '–' }}</td>
                <td>{{ s.schedule_year }}</td>
                <td>{{ s.planned_dates || '–' }}</td>
                <td>{{ s.notes || '–' }}</td>
                <td><button type="button" class="hr-btn hr-btn-sm" @click="editSchedule(s)">Edit</button></td>
              </tr>
              <tr v-if="!schedules.length"><td colspan="6" class="text-muted">No schedules</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-show="activeTab === 'terminal'" class="card-section">
        <div class="table-wrap">
          <table class="table table-bordered table-sm">
            <thead>
              <tr><th>Employee</th><th>Department</th><th>Retirement date</th><th>Terminal leave start</th><th>Terminal leave end</th><th>Days</th></tr>
            </thead>
            <tbody>
              <tr v-for="n in terminalNotifications" :key="n.id">
                <td>{{ n.firstName }} {{ n.lastName }}</td>
                <td>{{ n.empDept || n.department_id }}</td>
                <td>{{ n.retirement_date }}</td>
                <td>{{ n.terminal_leave_start || '–' }}</td>
                <td>{{ n.terminal_leave_end || '–' }}</td>
                <td>{{ n.days_granted || '–' }}</td>
              </tr>
              <tr v-if="!terminalNotifications.length"><td colspan="6" class="text-muted">No terminal leave notifications</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="showScheduleModal" class="modal-overlay" @click.self="showScheduleModal = false">
        <div class="modal-card">
          <h3>{{ editingSchedule ? 'Edit' : 'Add' }} schedule</h3>
          <div class="form-group">
            <label>Department</label>
            <select v-model="scheduleForm.department_id" required>
              <option value="">Select</option>
              <option v-for="d in departments" :key="d.id" :value="d.departmentID || d.id">{{ d.departmentName }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Leave type</label>
            <input v-model="scheduleForm.leave_type_name" type="text" placeholder="e.g. Annual">
          </div>
          <div class="form-group">
            <label>Planned dates (e.g. ranges)</label>
            <input v-model="scheduleForm.planned_dates" type="text" placeholder="e.g. 2026-01-01 to 2026-01-15">
          </div>
          <div class="form-group">
            <label>Notes</label>
            <input v-model="scheduleForm.notes" type="text">
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showScheduleModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveSchedule">Save</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

export default {
  name: "LeavePlanning",
  components: { HrPageLayout },
  data() {
    return {
      activeTab: "schedules",
      tabs: [
        { id: "schedules", label: "Department leave schedules" },
        { id: "terminal", label: "Terminal leave notifications" }
      ],
      scheduleYear: new Date().getFullYear(),
      yearOptions: [],
      filterDeptId: "",
      departments: [],
      schedules: [],
      terminalNotifications: [],
      showScheduleModal: false,
      editingSchedule: null,
      saving: false,
      scheduleForm: {
        id: null,
        department_id: "",
        department_name: "",
        schedule_year: new Date().getFullYear(),
        leave_type_name: "",
        planned_dates: "",
        notes: ""
      }
    };
  },
  created() {
    const y = new Date().getFullYear();
    for (let i = y; i <= y + 2; i++) this.yearOptions.push(i);
    this.getDepartments();
    this.load();
  },
  methods: {
    getDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments)
          this.departments = res.data.data.departments;
      }).catch(() => {});
    },
    load() {
      if (this.activeTab === "schedules") this.loadSchedules();
      else this.loadTerminal();
    },
    loadSchedules() {
      const fd = new FormData();
      fd.append("function", "getDepartmentLeaveSchedules");
      fd.append("year", this.scheduleYear);
      if (this.filterDeptId) fd.append("departmentId", this.filterDeptId);
      execute(fd, authUrl).then((res) => {
        this.schedules = (res.data && res.data.success && res.data.data && res.data.data.schedules) ? res.data.data.schedules : [];
      }).catch(() => { this.schedules = []; });
    },
    loadTerminal() {
      const fd = new FormData();
      fd.append("function", "getTerminalLeaveNotifications");
      execute(fd, authUrl).then((res) => {
        this.terminalNotifications = (res.data && res.data.success && res.data.data && res.data.data.notifications) ? res.data.data.notifications : [];
      }).catch(() => { this.terminalNotifications = []; });
    },
    openScheduleModal() {
      this.editingSchedule = null;
      this.scheduleForm = {
        id: null,
        department_id: "",
        department_name: "",
        schedule_year: this.scheduleYear,
        leave_type_name: "",
        planned_dates: "",
        notes: ""
      };
      this.showScheduleModal = true;
    },
    editSchedule(s) {
      this.editingSchedule = s;
      this.scheduleForm = {
        id: s.id,
        department_id: s.department_id,
        department_name: s.department_name,
        schedule_year: s.schedule_year,
        leave_type_name: s.leave_type_name || "",
        planned_dates: s.planned_dates || "",
        notes: s.notes || ""
      };
      this.showScheduleModal = true;
    },
    saveSchedule() {
      if (!this.scheduleForm.department_id) return;
      const d = this.departments.find((x) => (x.departmentID || x.id) == this.scheduleForm.department_id);
      this.scheduleForm.department_name = d ? d.departmentName : "";
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "saveDepartmentLeaveSchedule");
      Object.keys(this.scheduleForm).forEach((k) => fd.append(k, this.scheduleForm[k] == null ? "" : this.scheduleForm[k]));
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showScheduleModal = false;
          this.loadSchedules();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Save failed");
        }
      }).catch(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
.director-module { padding: 0 0 24px; }
.tabs-row { display: flex; gap: 8px; margin-bottom: 16px; }
.tab-btn { padding: 8px 16px; border: 1px solid #dee2e6; border-radius: 6px; background: #fff; cursor: pointer; }
.tab-btn.active { background: #0d6efd; color: #fff; border-color: #0d6efd; }
.card-section { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 16px; margin-bottom: 16px; }
.filters-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 16px; }
.filters-row label { font-weight: 600; }
.table-wrap { overflow-x: auto; }
.table { width: 100%; font-size: 13px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-card { background: #fff; border-radius: 8px; padding: 24px; max-width: 480px; width: 90%; }
.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-weight: 600; margin-bottom: 4px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
.text-muted { color: #6c757d; }
</style>
